/**
 * Navigation Configuration
 * Centralized navigation items for command palette and @go commands
 * 
 * Categories:
 * - pages: Main application pages
 * - settings: User and system settings
 * - admin: Administrative pages
 * 
 * requireGroups: Cognito groups that can access the page (empty = all users)
 * Suggested groups: Administrators, Quality, Procurement, Engineers
 */

import type { PagePanelId } from '../../types/panel-types';

export type NavigationCategory = 'pages' | 'settings' | 'admin';

export interface NavigationItem {
  path: string;
  panelId: PagePanelId;
  label: string;
  icon: string;
  category: NavigationCategory;
  requireGroups?: string[];
  keywords?: string[]; // Additional search terms for fuzzy matching
  isFullPage?: boolean; // If true, navigate to path instead of opening panel tab
}

export const navigationItems: NavigationItem[] = [
  // Pages - general access
  { 
    path: '/projects', 
    panelId: 'projects',
    label: 'Browse Projects', 
    icon: '📁',
    category: 'pages',
    keywords: ['projects', 'browse', 'all', 'search', 'list', 'workspace']
  },
  { 
    path: '/reference-library', 
    panelId: 'reference-library',
    label: 'References', 
    icon: '📖',
    category: 'pages',
    keywords: ['standards', 'documents', 'library', 'AC', 'DO-178']
  },
  { 
    path: '/company-documents', 
    panelId: 'company-docs',
    label: 'Company Docs', 
    icon: '📚',
    category: 'pages',
    keywords: ['policies', 'procedures', 'templates', 'internal']
  },
  { 
    path: '/wizard', 
    panelId: 'doc-control', // Fallback panel, but wizard uses full page
    label: 'Generate', 
    icon: '🎨',
    category: 'pages',
    keywords: ['create', 'new', 'document', 'wizard'],
    isFullPage: true, // Navigate to /wizard instead of opening panel
  },
  { 
    path: '/document-control', 
    panelId: 'doc-control',
    label: 'Doc Control', 
    icon: '🗂️',
    category: 'pages',
    keywords: ['control', 'revisions', 'approval']
  },
  { 
    path: '/crosswalk', 
    panelId: 'crosswalk',
    label: 'Crosswalk', 
    icon: '🔀',
    category: 'pages',
    keywords: ['requirements', 'mapping', 'traceability']
  },
  
  // Pages - role restricted
  { 
    path: '/quality', 
    panelId: 'quality',
    label: 'Quality', 
    icon: '📊',
    category: 'pages',
    requireGroups: ['Quality', 'Administrators'],
    keywords: ['QMS', 'metrics', 'compliance']
  },
  { 
    path: '/validation', 
    panelId: 'validation',
    label: 'Validation', 
    icon: '✓',
    category: 'pages',
    requireGroups: ['Quality', 'Administrators'],
    keywords: ['verify', 'check', 'requirements']
  },
  { 
    path: '/suppliers', 
    panelId: 'suppliers',
    label: 'Suppliers', 
    icon: '🏭',
    category: 'pages',
    requireGroups: ['Procurement', 'Administrators'],
    keywords: ['vendors', 'parts', 'procurement']
  },
  
  // Settings - all users
  { 
    path: '/my-profile', 
    panelId: 'my-profile',
    label: 'My Profile', 
    icon: '👤',
    category: 'settings',
    keywords: ['account', 'user', 'name', 'email', 'password']
  },
  { 
    path: '/preferences', 
    panelId: 'preferences',
    label: 'Preferences', 
    icon: '⚙️',
    category: 'settings',
    keywords: ['settings', 'theme', 'dark', 'light', 'notifications']
  },
  
  // Settings - admin only
  { 
    path: '/organization', 
    panelId: 'organization',
    label: 'Organization', 
    icon: '🏢',
    category: 'settings',
    requireGroups: ['Administrators'],
    keywords: ['company', 'team', 'settings']
  },
  { 
    path: '/billing', 
    panelId: 'billing',
    label: 'Billing', 
    icon: '💳',
    category: 'settings',
    requireGroups: ['Administrators'],
    keywords: ['payment', 'subscription', 'invoice']
  },
  
  // Admin
  { 
    path: '/users', 
    panelId: 'users',
    label: 'Users', 
    icon: '👥',
    category: 'admin',
    requireGroups: ['Administrators'],
    keywords: ['manage', 'team', 'permissions', 'roles']
  },
  { 
    path: '/reliability-data', 
    panelId: 'reliability-admin',
    label: 'Reliability Data', 
    icon: '📈',
    category: 'admin',
    requireGroups: ['Administrators', 'Engineers'],
    keywords: ['failure rates', 'MIL-HDBK-217', 'FMEA', 'FTA', 'reliability', 'MTBF']
  },
];

/**
 * Filter navigation items based on user's Cognito groups
 */
export function filterNavigationByGroups(
  items: NavigationItem[],
  userGroups: string[]
): NavigationItem[] {
  return items.filter(item => {
    // No requireGroups means accessible to all
    if (!item.requireGroups || item.requireGroups.length === 0) {
      return true;
    }
    // Check if user has any of the required groups
    return item.requireGroups.some(group => userGroups.includes(group));
  });
}

/**
 * Get navigation items by category
 */
export function getNavigationByCategory(
  items: NavigationItem[],
  category: NavigationCategory
): NavigationItem[] {
  return items.filter(item => item.category === category);
}

/**
 * Simple fuzzy search across label and keywords
 */
export function searchNavigation(
  items: NavigationItem[],
  query: string
): NavigationItem[] {
  if (!query.trim()) return items;
  
  const lowerQuery = query.toLowerCase();
  
  return items.filter(item => {
    const labelMatch = item.label.toLowerCase().includes(lowerQuery);
    const keywordMatch = item.keywords?.some(k => k.toLowerCase().includes(lowerQuery));
    return labelMatch || keywordMatch;
  }).sort((a, b) => {
    // Prioritize label matches over keyword matches
    const aLabelMatch = a.label.toLowerCase().includes(lowerQuery);
    const bLabelMatch = b.label.toLowerCase().includes(lowerQuery);
    if (aLabelMatch && !bLabelMatch) return -1;
    if (!aLabelMatch && bLabelMatch) return 1;
    return 0;
  });
}
